import { ErrorCode } from '@kreart/shared';
import type { TFunction } from 'i18next';
import { ApiError, NetworkError } from '@/lib/api';
import { knownServerMessage } from './serverMessages';

/**
 * Every error the UI shows says WHAT went wrong — never "something went wrong".
 *
 * The pieces, in the order they are tried:
 *   1. a feature's own code mapping (USERS_EMAIL_TAKEN → its sentence), for
 *      codes that name the problem by themselves;
 *   2. the server's own sentence, recognised in the catalog and explained in
 *      the user's words (`serverMessages.ts`) — the API often refuses under a
 *      code as broad as CONFLICT or NOT_FOUND, and only the sentence tells one
 *      refusal from another;
 *   3. codes whose text needs nothing more (an expired session, a lock-out);
 *   4. the fields a validation failure names, each with its reason;
 *   5. the kind of refusal, quoting the server's sentence when it has one;
 *   6. an error in the app itself, with its message — what it really says.
 */

/**
 * What a resolver answers with: an i18n key, or a key with the values that fill
 * its placeholders. A value may itself be an {@link ErrorMessage} — a reason
 * inside a sentence — or a list of them, formatted in turn; `stages` names the
 * values that are operation codes, shown by the operation's name.
 */
export type ErrorMessage =
  | string
  | { key: string; values?: Record<string, unknown>; stages?: readonly string[] };

/** Resolves an error to the message that explains it. */
export type ErrorResolver = (err: unknown) => ErrorMessage;

export type ErrorKeyMap = Partial<Record<ErrorCode, string>>;

/** Codes whose own text says everything the user needs. */
const SELF_EXPLANATORY: ErrorKeyMap = {
  [ErrorCode.AUTH_INVALID_CREDENTIALS]: 'errors.invalidCredentials',
  [ErrorCode.AUTH_ACCOUNT_LOCKED]: 'errors.accountLocked',
  [ErrorCode.AUTH_PASSWORD_CHANGE_REQUIRED]: 'errors.passwordChangeRequired',
  [ErrorCode.AUTH_TOKEN_EXPIRED]: 'errors.sessionExpired',
  [ErrorCode.AUTH_TOKEN_INVALID]: 'errors.sessionExpired',
  [ErrorCode.AUTH_CSRF_MISMATCH]: 'errors.csrfMismatch',
  [ErrorCode.PASSWORD_TOO_WEAK]: 'errors.passwordTooWeak',
  [ErrorCode.RATE_LIMITED]: 'errors.rateLimited',
  [ErrorCode.INTERNAL_ERROR]: 'errors.serverError',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'errors.serviceUnavailable',
};

/**
 * Codes too broad to explain anything by themselves. A feature may still map
 * one (NOT_FOUND → "the cabinet is gone"), but that is only its best guess,
 * used when the server said nothing more precise.
 */
const BROAD_CODES = new Set<string>([
  ErrorCode.NOT_FOUND,
  ErrorCode.CONFLICT,
  ErrorCode.VALIDATION_FAILED,
  ErrorCode.FORBIDDEN,
  ErrorCode.ALREADY_EXISTS,
]);

/** The sentences the API (or something in front of it) sends when it has nothing to say. */
const UNINFORMATIVE =
  /^(validation failed\.?|internal server error\.?|not found|forbidden( resource)?|conflict|bad request|unauthorized|request failed|http \d{3})$/i;

/**
 * The server's sentence, when it carries one worth showing — not a placeholder,
 * and not a page some proxy answered with (HTML, or far longer than a sentence).
 */
function informativeMessage(err: ApiError): string | null {
  const message = err.message?.trim();
  if (!message || UNINFORMATIVE.test(message)) return null;
  if (message.startsWith('<') || message.length > 400) return null;
  return message;
}

/** The server sentence as an explanation: the catalog's words, else quoted as sent. */
function sentenceOf(message: string): ErrorMessage {
  const known = knownServerMessage(message);
  return known
    ? { key: known.key, values: known.values, stages: known.stages }
    : { key: 'errors.quoted', values: { message } };
}

/**
 * What a VALIDATION_FAILED names (`details.issues`, zod's flatten): one entry
 * per field (`field — reason`), then the form-level reasons.
 */
function validationIssues(err: ApiError): ErrorMessage[] {
  if (err.code !== ErrorCode.VALIDATION_FAILED) return [];
  const issues = (err.details as { issues?: unknown } | undefined)?.issues as
    | { formErrors?: unknown; fieldErrors?: unknown }
    | undefined;
  if (!issues || typeof issues !== 'object') return [];
  const out: ErrorMessage[] = [];
  const fieldErrors = issues.fieldErrors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    for (const [field, messages] of Object.entries(fieldErrors as Record<string, unknown>)) {
      if (!Array.isArray(messages)) continue;
      for (const message of messages) {
        if (typeof message !== 'string') continue;
        out.push({ key: 'errors.validationIssue', values: { field, reason: sentenceOf(message) } });
      }
    }
  }
  if (Array.isArray(issues.formErrors)) {
    for (const message of issues.formErrors) {
      if (typeof message === 'string') out.push(sentenceOf(message));
    }
  }
  return out;
}

/** What went wrong with a request the API answered. */
function explainApiError(err: ApiError): ErrorMessage {
  // Something between the browser and the API answered instead of it (dev
  // proxy, gateway, a crashed process): say which side failed.
  if (err.status === 502 || err.status === 503 || err.status === 504) {
    return err.code === ErrorCode.SERVICE_UNAVAILABLE
      ? 'errors.serviceUnavailable'
      : 'errors.unreachable';
  }
  const message = informativeMessage(err);
  const known = message ? knownServerMessage(message) : null;
  if (known) return { key: known.key, values: known.values, stages: known.stages };
  const self = SELF_EXPLANATORY[err.code as ErrorCode];
  if (self) return self;
  if (err.status >= 500) return 'errors.serverError';
  const issues = validationIssues(err);
  if (issues.length > 0) return { key: 'errors.validationIssues', values: { issues } };
  if (err.status === 413) return 'errors.tooLarge';
  if (err.status === 429) return 'errors.rateLimited';
  if (err.status === 401) return 'errors.sessionExpired';
  if (!message) {
    switch (err.status) {
      case 400:
      case 422:
        return 'errors.validation';
      case 403:
        return 'errors.forbidden';
      case 404:
        return 'errors.notFound';
      case 409:
        return 'errors.conflict';
      default:
        return { key: 'errors.status', values: { status: err.status } };
    }
  }
  switch (err.status) {
    case 400:
    case 422:
      return { key: 'errors.rejectedBecause', values: { message } };
    case 403:
      return { key: 'errors.forbiddenBecause', values: { message } };
    case 404:
      return { key: 'errors.notFoundBecause', values: { message } };
    case 409:
      return { key: 'errors.conflictBecause', values: { message } };
    default:
      return { key: 'errors.statusBecause', values: { status: err.status, message } };
  }
}

/** Whether the server said what went wrong — a sentence of its own, or the fields it refused. */
function hasOwnReason(err: ApiError): boolean {
  return informativeMessage(err) !== null || validationIssues(err).length > 0;
}

/** What went wrong, for any error at all. */
export function explainError(err: unknown): ErrorMessage {
  // Nothing answered at all: the fix is on the user's side of the wire (or the
  // API is down), not in the request.
  if (err instanceof NetworkError) return err.offline ? 'errors.offline' : 'errors.unreachable';
  if (err instanceof ApiError) return explainApiError(err);
  // A fault in the app itself: its own message is the only honest explanation.
  if (err instanceof Error) {
    return { key: 'errors.client', values: { message: err.message || err.name } };
  }
  return { key: 'errors.client', values: { message: String(err) } };
}

/**
 * Build a feature-aware resolver. `overrides` maps the feature's codes to its
 * sentences; a code that names the problem itself always wins. A broad code it
 * maps (see {@link BROAD_CODES}) is used only when the server gave no reason of
 * its own — otherwise the server's reason is explained instead.
 */
export function makeErrorKeyResolver(overrides: ErrorKeyMap = {}): ErrorResolver {
  return (err: unknown) => {
    if (err instanceof ApiError) {
      const own = overrides[err.code as ErrorCode];
      if (own && (!BROAD_CODES.has(err.code) || !hasOwnReason(err))) return own;
    }
    return explainError(err);
  };
}

/** Default resolver with no feature overrides — safe shared baseline. */
export const errorKeyFor = makeErrorKeyResolver();

/**
 * What failed, then why: `key`'s sentence with the error's explanation as its
 * `{{reason}}` — for a feature that names the action it was attempting.
 */
export function failedBecause(
  key: string,
  err: unknown,
  resolve: ErrorResolver = errorKeyFor,
): ErrorMessage {
  const reason = resolve(err);
  return { key, values: { reason: typeof reason === 'string' ? { key: reason } : reason } };
}

function isErrorMessageObject(value: unknown): value is Exclude<ErrorMessage, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { key?: unknown }).key === 'string'
  );
}

function formatValue(t: TFunction, value: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => formatValue(t, item)).join('; ');
  if (isErrorMessageObject(value)) return formatErrorMessage(t, value);
  return value;
}

/** The text of an {@link ErrorMessage}, with nested reasons and operation names filled in. */
export function formatErrorMessage(t: TFunction, message: ErrorMessage): string {
  if (typeof message === 'string') return t(message);
  const values: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(message.values ?? {})) {
    values[name] = formatValue(t, value);
  }
  for (const name of message.stages ?? []) {
    const code = values[name];
    if (typeof code === 'string') {
      values[name] = t(`operationsScheduler.stages.${code}`, { defaultValue: code });
    }
  }
  return t(message.key, values);
}

/**
 * A sentence the server sent outside an error response — a row an import
 * refused, an item a recompute skipped — in the user's words.
 */
export function describeServerSentence(t: TFunction, message: string): string {
  return formatErrorMessage(t, sentenceOf(message));
}

/** The text explaining an error, through `resolve` (the shared baseline by default). */
export function describeError(
  t: TFunction,
  err: unknown,
  resolve: ErrorResolver = errorKeyFor,
): string {
  return formatErrorMessage(t, resolve(err));
}
